import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, requestJson } from './client';
import { authHeaders } from './regulations';

export interface KpiPeriod {
  from: string;
  to: string;
}

export interface KpiItem {
  name: string;
  unit: string | null;
  direction: 'up' | 'down';
  plan: number;
  /** null — факт ещё не внесён. */
  fact: number | null;
  completion: number | null;
}

export interface EmployeeKpi {
  periods: KpiPeriod[];
  period: KpiPeriod | null;
  items: KpiItem[];
  total: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** Заполнять может только непосредственный руководитель. */
  canEdit: boolean;
}

export interface KpiItemInput {
  name: string;
  unit: string | null;
  direction: 'up' | 'down';
  plan: number;
  fact: number | null;
}

export interface FineReason {
  title: string;
  amountKopecks: number;
}

export interface Payout {
  id: string;
  paidOn: string;
  periodFrom: string;
  periodTo: string;
}

export interface EmployeeFines {
  reasons: FineReason[];
  payouts: Payout[];
  payout: (Payout & { nextUpdate: string }) | null;
  items: { id: string; occurredOn: string | null; reason: string; amountKopecks: number }[];
  totalKopecks: number;
}

export interface ImportResult {
  kind: 'fines';
  fileName: string;
  period: { from: string; to: string };
  paidOn: string | null;
  rows: number;
  employees: number;
  scopeEmployees: number;
  replaces: number;
  errorCount: number;
  errors: { row: number; message: string }[];
  preview: Record<string, unknown>[];
  committed: boolean;
}

export interface DataImport {
  id: string;
  kind: 'kpi' | 'fines';
  fileName: string;
  periodFrom: string;
  periodTo: string;
  paidOn: string | null;
  rows: number;
  employees: number;
  replaced: number;
  uploadedBy: string;
  createdAt: string;
}

export function useEmployeeKpi(employeeId: string, period: KpiPeriod | null) {
  return useQuery({
    queryKey: ['kpi', employeeId, period?.from, period?.to],
    queryFn: () =>
      requestJson<EmployeeKpi>(
        `/api/v1/employees/${employeeId}/kpi${period ? `?from=${period.from}&to=${period.to}` : ''}`,
      ),
  });
}

export function useSaveKpi(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: KpiPeriod & { items: KpiItemInput[] }) =>
      requestJson<EmployeeKpi>(`/api/v1/employees/${employeeId}/kpi`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kpi', employeeId] }),
  });
}

export function useEmployeeFines(employeeId: string, payoutId: string | null) {
  return useQuery({
    queryKey: ['fines', employeeId, payoutId],
    queryFn: () =>
      requestJson<EmployeeFines>(
        `/api/v1/employees/${employeeId}/fines${payoutId ? `?payoutId=${payoutId}` : ''}`,
      ),
  });
}

export function useFineReasons(roleGroupId: string | undefined) {
  return useQuery({
    queryKey: ['fine-reasons', roleGroupId],
    queryFn: () =>
      requestJson<{ items: FineReason[]; canEdit: boolean }>(
        `/api/v1/role-groups/${roleGroupId}/fine-reasons`,
      ),
    enabled: Boolean(roleGroupId),
  });
}

export function useSaveFineReasons(roleGroupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: FineReason[]) =>
      requestJson<{ items: FineReason[]; canEdit: boolean }>(
        `/api/v1/role-groups/${roleGroupId}/fine-reasons`,
        {
          method: 'PUT',
          body: JSON.stringify({ items }),
        },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['fine-reasons', roleGroupId], data);
      void queryClient.invalidateQueries({ queryKey: ['fines'] });
    },
  });
}

export function useImports() {
  return useQuery({
    queryKey: ['imports'],
    queryFn: () => requestJson<{ items: DataImport[] }>('/api/v1/imports'),
  });
}

/** Проверка или загрузка файла штрафов: тело — сам файл, имя — в заголовке. */
export async function sendFinesImport(
  params: Record<string, string>,
  file: File,
  commit: boolean,
): Promise<ImportResult> {
  const query = new URLSearchParams({ ...params, commit: String(commit) });
  const response = await fetch(`/api/v1/imports/fines?${query}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-goodway-csrf': '1',
      ...authHeaders(),
    },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | (ImportResult & { error?: { message?: string } })
    | null;
  if (!response.ok) {
    throw new ApiError(response.status, 'BAD_REQUEST', body?.error?.message ?? 'Файл не обработан');
  }
  return body as ImportResult;
}

const rubles = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
export const formatRubles = (kopecks: number) => `${rubles.format(kopecks / 100)} ₽`;

const decimal = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
export const formatValue = (value: number) => decimal.format(value);

export const formatIsoDate = (value: string | null) =>
  value ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}` : '—';

/** Обновление запросов после загрузки файла. */
export function useInvalidateRecords() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(['fines', 'imports'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
}
